/**
 * @file     RealtorCaViewBeacon.tsx
 * @purpose  Fires a CREA DDF Analytics "View" event when a DDF-sourced listing
 *           detail page is shown (DDF Rule 5(c)). Client-side so CREA resolves
 *           the real visitor IP; a stable per-device UUID lives in localStorage.
 *           No-op for non-DDF listings or when the Destination ID isn't set.
 *
 * The endpoint is a fire-and-forget GET beacon; CREA ignores duplicate
 * UUID+event within a 5-minute window, so re-renders are harmless.
 */

'use client';

import { useEffect } from 'react';

const ENDPOINT = 'https://analytics.crea.ca/LogEvents.svc/LogEvents';
const UUID_KEY = 'relta.ddfUuid.v1';

function getOrCreateUuid(): string {
  try {
    let id = localStorage.getItem(UUID_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(UUID_KEY, id);
    }
    return id;
  } catch {
    return crypto.randomUUID();
  }
}

type Props = {
  feedProvider: string | null;
  /** DDF ListingKey. */
  listingKey: string;
  /** Public Destination ID, injected from NEXT_PUBLIC_DDF_DESTINATION_ID. */
  destinationId: string | null;
};

export function RealtorCaViewBeacon({ feedProvider, listingKey, destinationId }: Props) {
  useEffect(() => {
    if (feedProvider !== 'ddf' || !destinationId || !listingKey) return;
    const uuid = getOrCreateUuid();
    const params = new URLSearchParams({
      ListingID: listingKey,
      DestinationID: destinationId,
      EventType: 'View',
      UUID: `${uuid}-${destinationId}`,
      ReferralURL: typeof document !== 'undefined' ? document.referrer || location.href : '',
    });
    // Image-beacon style GET; no response handling required.
    void fetch(`${ENDPOINT}?${params.toString()}`, {
      method: 'GET',
      mode: 'no-cors',
      keepalive: true,
    }).catch(() => {});
  }, [feedProvider, listingKey, destinationId]);

  return null;
}
